import api from './api';
import { saveAs } from '../utils/saveFile';

// Order Update via Excel importer (360 port of v1 imports/orderupdate.pl).
// Endpoints live under /orders/order-update-import/*. The axios `api` instance
// already carries the /api/v1 prefix.
const BASE = '/orders/order-update-import';

export interface FieldDescriptor {
  label: string;
  appendable: boolean;
  kind: string;
}

export interface ReferenceData {
  fields: FieldDescriptor[];
  equipment_sizes: string[];
  equipment_types: string[];
}

export interface RowError {
  row: number;
  field: string;
  message: string;
}

export interface EnqueueResponse {
  job_uuid: string;
  status: string;
  total_rows: number;
  warnings: RowError[];
}

export interface ImportRowStatus {
  row_number: number;
  pronumber: string;
  status: string; // pending | ok | skipped | error
  result_code: string;
  detail: string;
}

export interface ImportJobStatus {
  job_uuid: string;
  status: string; // queued | running | succeeded | partial | failed
  created_by: string;
  created_at?: string | null;
  filename: string;
  scope_type: string;
  append_option: string;
  total_rows: number;
  processed_rows: number;
  succeeded_rows: number;
  failed_rows: number;
  skipped_rows: number;
  error_summary: string;
  rows: ImportRowStatus[];
}

export type ScopeType = 'customer' | 'billto';
export type AppendOption = 'APPEND' | 'UPDATE';

export interface ApplyParams {
  file: File;
  scopeType: ScopeType;
  customercode?: number | null;
  billto?: number | null;
  appendOption: AppendOption;
  fields: string[];
}

export const TERMINAL_STATUSES = ['succeeded', 'partial', 'failed'];

export const orderUpdateImportService = {
  async getReferenceData(): Promise<ReferenceData> {
    const { data } = await api.get<ReferenceData>(`${BASE}/reference-data`);
    return data;
  },

  /** POST the selected fields, get a blank .xlsx back, and trigger a download. */
  async downloadTemplate(fields: string[]): Promise<void> {
    await saveAs('order-update-template.xlsx', async () => {
      const response = await api.post(
        `${BASE}/template`,
        { fields },
        { responseType: 'blob' },
      );
      return new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
    });
  },

  /** Upload the filled spreadsheet; returns a job_uuid (202) to poll. */
  async apply(params: ApplyParams): Promise<EnqueueResponse> {
    const fd = new FormData();
    fd.append('file', params.file);
    fd.append('scope_type', params.scopeType);
    if (params.scopeType === 'customer' && params.customercode != null) {
      fd.append('customercode', String(params.customercode));
    }
    if (params.scopeType === 'billto' && params.billto != null) {
      fd.append('billto', String(params.billto));
    }
    fd.append('append_option', params.appendOption);
    fd.append('fields', JSON.stringify(params.fields));
    const { data } = await api.post<EnqueueResponse>(`${BASE}/apply`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  async getStatus(jobUuid: string): Promise<ImportJobStatus> {
    const { data } = await api.get<ImportJobStatus>(`${BASE}/status/${jobUuid}`);
    return data;
  },
};

export default orderUpdateImportService;
